import { useCallback, useMemo, useState } from 'react';
import { formatInTimeZone } from 'date-fns-tz';
import { Form } from 'react-bootstrap';
import {
  MdArrowBack,
  MdArrowDownward,
  MdArrowUpward,
  MdClose,
  MdFileDownload
} from 'react-icons/md';
import { YouTubeCreatorSetWithMembers } from '@blisspointmedia/bpm-types/dist/YoutubeCreatorSets';
import { YouTubeCreatorSetMember } from '@blisspointmedia/bpm-types/dist/pgTables/YoutubeCreatorSetMember';
import { Button } from '@Components/Button';
import { Appearance, Design, Size } from '@Components/designConstants';
import { IconButton } from '@Components/IconButton';
import { InfoTooltip } from '@Components/InfoTooltip';
import { BPMTable, TypedHeader } from '@Components/StickyTable';
import { TableEmptyState } from '@Components/TableEmptyState';
import { formatLastEdited } from '@Pages/StreamingMediaPlanner/streaming-media-planner-utils';
import { useSetError } from '@src/redux/modals';
import { exportMultiSheetToExcel } from '@src/utils/download-utils';
import { useCompanyInfo } from '@src/utils/hooks/useCompanyInfo';
import { Mixpanel, MxE } from '@src/utils/mixpanelWrapper';
import { conditionallyPluralizeWithCount } from '@src/utils/text-utils';
import { PAGE_TITLE } from '../youtube-planning-constants';
import { BuildRecommendationPrefill, GoogleAdsAccount } from '../youtube-planning-types';
import { CreatorSetAttributes } from './CreatorSetAttributes';
import { CreatorSetSnapshot } from './CreatorSetSnapshot';
import { RefreshMetricsModal } from './RefreshMetricsModal';
import {
  CREATOR_SET_ENTITY,
  EXPORT_ACTION,
  MemberMetricsChange,
  SHARES_VIEWER_DATA_TOOLTIP
} from './creator-sets-constants';
import { toCreatorSetFileName, toCreatorSetSheets } from './creator-set-export-utils';
import {
  applyMetricsChanges,
  formatCount,
  formatSetDate,
  moveMember,
  sharesViewerData,
  toEpochMs,
  toSaveInput
} from './creator-sets-utils';
import { useRefreshCreatorMetrics, useSaveCreatorSet } from './hooks/use-creator-sets';

const ROW_HEIGHT = 56;

interface CreatorSetDetailProps {
  set: YouTubeCreatorSetWithMembers;
  adsAccount?: GoogleAdsAccount;
  onBack: () => void;
  onUseInBuild: (prefill: BuildRecommendationPrefill) => void;
}

// Edits are buffered: nothing is written until Save, and Discard restores the saved set.
export const CreatorSetDetail = ({
  set,
  adsAccount,
  onBack,
  onUseInBuild
}: CreatorSetDetailProps) => {
  const { cid } = useCompanyInfo();
  const setError = useSetError();
  const { mutateAsync: saveSet, isPending: saving } = useSaveCreatorSet(cid);
  const { mutateAsync: refreshMetrics, isPending: refreshing } = useRefreshCreatorMetrics(
    cid,
    adsAccount
  );

  const [isEditMode, setIsEditMode] = useState(false);
  const [nameDraft, setNameDraft] = useState(set.name);
  const [descriptionDraft, setDescriptionDraft] = useState(set.description ?? '');
  const [membersDraft, setMembersDraft] = useState(set.members);
  const [metricsChanges, setMetricsChanges] = useState<MemberMetricsChange[]>();
  const [exporting, setExporting] = useState(false);

  const onEnterEdit = () => {
    setNameDraft(set.name);
    setDescriptionDraft(set.description ?? '');
    setMembersDraft(set.members);
    setIsEditMode(true);
  };

  const onDiscardChanges = () => setIsEditMode(false);

  const onSaveChanges = async () => {
    try {
      await saveSet(
        toSaveInput(set, {
          name: nameDraft.trim(),
          description: descriptionDraft.trim() || null,
          members: membersDraft
        })
      );
      setIsEditMode(false);
      Mixpanel.track(MxE.RecordSaved, { page: PAGE_TITLE, entity: CREATOR_SET_ENTITY });
    } catch (e) {
      setError({ message: e.message, reportError: e });
    }
  };

  const onRefresh = async () => {
    try {
      setMetricsChanges(await refreshMetrics(set.members));
    } catch (e) {
      setError({ title: "Couldn't refresh metrics", message: e.message, reportError: e });
    }
  };

  const onSaveRefreshed = async () => {
    if (!metricsChanges) return;
    try {
      await saveSet(
        toSaveInput(set, { members: applyMetricsChanges(set.members, metricsChanges) })
      );
      setMetricsChanges(undefined);
      Mixpanel.track(MxE.RecordSaved, { page: PAGE_TITLE, entity: CREATOR_SET_ENTITY });
    } catch (e) {
      setError({ message: e.message, reportError: e });
    }
  };

  const onExport = async () => {
    setExporting(true);
    try {
      const exportedOn = formatInTimeZone(new Date(), 'America/New_York', 'yyyy-MM-dd');
      await exportMultiSheetToExcel(
        toCreatorSetSheets(set, exportedOn),
        toCreatorSetFileName(set.company, set.name, exportedOn)
      );
      Mixpanel.track(MxE.ActionTriggered, { page: PAGE_TITLE, action: EXPORT_ACTION });
    } catch (e) {
      setError({ title: "Couldn't export the set", message: e.message, reportError: e });
    } finally {
      setExporting(false);
    }
  };

  const onMoveMember = useCallback(
    (channelId: string, offset: number) =>
      setMembersDraft(prev =>
        moveMember(
          prev,
          prev.findIndex(member => member.channel_id === channelId),
          offset
        )
      ),
    []
  );

  const onRemoveMember = useCallback(
    (channelId: string) =>
      setMembersDraft(prev => prev.filter(member => member.channel_id !== channelId)),
    []
  );

  const members = isEditMode ? membersDraft : set.members;

  return (
    <div className='creator-sets-card' data-testid='creator-set-detail'>
      <div className='creator-set-detail-header'>
        <Button
          appearance={Appearance.EMPTY}
          design={Design.NEUTRAL}
          leadingElement={<MdArrowBack />}
          disabled={isEditMode}
          onClick={onBack}
        >
          Back to creator sets
        </Button>
        <div className='creator-set-detail-title-row'>
          <SetTitle
            set={set}
            isEditMode={isEditMode}
            saving={saving}
            nameDraft={nameDraft}
            descriptionDraft={descriptionDraft}
            onNameChange={setNameDraft}
            onDescriptionChange={setDescriptionDraft}
          />
          <div className='creator-set-detail-actions'>
            {isEditMode ? (
              <>
                <Button
                  size={Size.LG}
                  appearance={Appearance.EMPTY}
                  design={Design.NEUTRAL}
                  disabled={saving}
                  onClick={onDiscardChanges}
                >
                  Discard changes
                </Button>
                <Button
                  size={Size.LG}
                  disabled={!nameDraft.trim()}
                  loading={saving}
                  onClick={onSaveChanges}
                >
                  Save changes
                </Button>
              </>
            ) : (
              <>
                <Button
                  size={Size.LG}
                  appearance={Appearance.OUTLINED}
                  disabled={!adsAccount || set.members.length === 0}
                  loading={refreshing}
                  onClick={onRefresh}
                >
                  Refresh metrics
                </Button>
                <Button
                  size={Size.LG}
                  appearance={Appearance.OUTLINED}
                  leadingElement={<MdFileDownload />}
                  disabled={set.members.length === 0}
                  loading={exporting}
                  onClick={onExport}
                >
                  Export
                </Button>
                <Button size={Size.LG} onClick={onEnterEdit}>
                  Edit set
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
      <div className='creator-set-detail-body'>
        {/* Not while editing: leaving the tab would drop the unsaved edits. */}
        <CreatorSetAttributes
          members={members}
          onUseInBuild={isEditMode ? undefined : onUseInBuild}
        />
        <div className='creator-set-members-table' data-testid='creator-set-members'>
          <MembersTable
            members={members}
            isEditMode={isEditMode}
            saving={saving}
            onMove={onMoveMember}
            onRemove={onRemoveMember}
          />
        </div>
        <CreatorSetSnapshot members={members} />
      </div>
      {metricsChanges && (
        <RefreshMetricsModal
          changes={metricsChanges}
          saving={saving}
          onSave={onSaveRefreshed}
          onCancel={() => setMetricsChanges(undefined)}
        />
      )}
    </div>
  );
};

interface SetTitleProps {
  set: YouTubeCreatorSetWithMembers;
  isEditMode: boolean;
  // Drafts are locked while a save is in flight so nothing typed is silently dropped.
  saving: boolean;
  nameDraft: string;
  descriptionDraft: string;
  onNameChange: (name: string) => void;
  onDescriptionChange: (description: string) => void;
}

const SetTitle = ({
  set,
  isEditMode,
  saving,
  nameDraft,
  descriptionDraft,
  onNameChange,
  onDescriptionChange
}: SetTitleProps) => {
  if (isEditMode) {
    return (
      <div className='creator-set-detail-title'>
        <Form.Control
          className='creator-set-name-input'
          data-testid='creator-set-name-input'
          value={nameDraft}
          disabled={saving}
          placeholder='Creator set name'
          onChange={e => onNameChange(e.target.value)}
        />
        <Form.Control
          className='creator-set-description-input'
          value={descriptionDraft}
          disabled={saving}
          placeholder='Description (optional)'
          onChange={e => onDescriptionChange(e.target.value)}
        />
      </div>
    );
  }
  return (
    <div className='creator-set-detail-title'>
      <div className='header' data-testid='creator-set-name'>
        {set.name}
      </div>
      {set.description && <div className='creator-set-description'>{set.description}</div>}
      <div className='creator-set-meta'>
        {conditionallyPluralizeWithCount(set.members.length, 'creator')} · Last edited by{' '}
        {set.lastuser} on {formatLastEdited(toEpochMs(set.lastmodified))}
      </div>
    </div>
  );
};

interface MembersTableProps {
  members: YouTubeCreatorSetMember[];
  isEditMode: boolean;
  saving: boolean;
  onMove: (channelId: string, offset: number) => void;
  onRemove: (channelId: string) => void;
}

const MembersTable = ({ members, isEditMode, saving, onMove, onRemove }: MembersTableProps) => {
  // Every column is nonInteractive so header clicks can't sort away the curated order.
  const headers = useMemo<TypedHeader<YouTubeCreatorSetMember>[]>(() => {
    const columns: TypedHeader<YouTubeCreatorSetMember>[] = [
      {
        name: 'position',
        label: '#',
        width: 56,
        nonInteractive: true,
        renderer: member => members.indexOf(member) + 1
      },
      {
        name: 'creator',
        label: 'Creator',
        flex: 1,
        nonInteractive: true,
        renderer: member => <CreatorCell member={member} />
      },
      {
        name: 'subscribers',
        label: 'Subscribers',
        width: 140,
        nonInteractive: true,
        renderer: member => formatCount(member.insights.subscriber_count)
      },
      {
        name: 'views',
        label: 'Views',
        width: 160,
        nonInteractive: true,
        renderer: member => formatCount(member.insights.views_count)
      },
      {
        name: 'sharesViewerData',
        label: 'Shares Viewer Data',
        width: 200,
        nonInteractive: true,
        renderer: member => (sharesViewerData(member) ? 'Yes' : 'No')
      },
      {
        name: 'added',
        label: 'Added',
        width: 140,
        nonInteractive: true,
        renderer: member => formatSetDate(member.added_at)
      },
      {
        name: 'snapshotted',
        label: 'Metrics As Of',
        width: 140,
        nonInteractive: true,
        renderer: member => formatSetDate(member.insights.snapshotted_at)
      }
    ];
    if (!isEditMode) return columns;
    return [
      ...columns,
      {
        name: 'actions',
        label: '',
        width: 128,
        nonInteractive: true,
        nonFilterable: true,
        renderer: member => (
          <MemberActions
            member={member}
            disabled={saving}
            isFirst={members.indexOf(member) === 0}
            isLast={members.indexOf(member) === members.length - 1}
            onMove={onMove}
            onRemove={onRemove}
          />
        )
      }
    ];
  }, [members, isEditMode, saving, onMove, onRemove]);

  const renderHeader = useCallback(
    (cell: { data: unknown; columnIndex: number }) => {
      const label = typeof cell.data === 'string' ? cell.data : null;
      if (headers[cell.columnIndex]?.name !== 'sharesViewerData') return <>{label}</>;
      return (
        <div className='shares-viewer-data-header'>
          {label}
          <InfoTooltip size={Size.SM}>{SHARES_VIEWER_DATA_TOOLTIP}</InfoTooltip>
        </div>
      );
    },
    [headers]
  );

  const renderNoRows = useCallback(
    () => (
      <TableEmptyState message='No creators in this set. Add some from the Discover Creators tab.' />
    ),
    []
  );

  return (
    <BPMTable
      data={members}
      headers={headers}
      headersRenderer={renderHeader}
      autoHeight
      filterBar={false}
      rowHeight={ROW_HEIGHT}
      noRowsRenderer={renderNoRows}
    />
  );
};

const CreatorCell = ({ member }: { member: YouTubeCreatorSetMember }) => (
  <div className='creator-cell'>
    {member.insights.thumbnail_url && (
      <img className='creator-thumbnail' src={member.insights.thumbnail_url} alt='' />
    )}
    <a
      className='creator-name'
      href={`https://www.youtube.com/channel/${member.channel_id}`}
      target='_blank'
      rel='noreferrer'
    >
      {member.insights.display_name}
    </a>
  </div>
);

interface MemberActionsProps {
  member: YouTubeCreatorSetMember;
  disabled: boolean;
  isFirst: boolean;
  isLast: boolean;
  onMove: (channelId: string, offset: number) => void;
  onRemove: (channelId: string) => void;
}

const MemberActions = ({
  member,
  disabled,
  isFirst,
  isLast,
  onMove,
  onRemove
}: MemberActionsProps) => (
  <div className='creator-set-member-actions' data-testid={`member-actions-${member.channel_id}`}>
    <IconButton
      size={Size.SM}
      aria-label='Move up'
      tooltip={{ text: 'Move up' }}
      disabled={disabled || isFirst}
      onClick={() => onMove(member.channel_id, -1)}
    >
      <MdArrowUpward />
    </IconButton>
    <IconButton
      size={Size.SM}
      aria-label='Move down'
      tooltip={{ text: 'Move down' }}
      disabled={disabled || isLast}
      onClick={() => onMove(member.channel_id, 1)}
    >
      <MdArrowDownward />
    </IconButton>
    <IconButton
      size={Size.SM}
      aria-label='Remove'
      tooltip={{ text: 'Remove from set' }}
      disabled={disabled}
      onClick={() => onRemove(member.channel_id)}
    >
      <MdClose />
    </IconButton>
  </div>
);
